import { Button, makeStyles, shorthands } from "@fluentui/react-components";
import { Delete20Regular, Add20Regular } from "@fluentui/react-icons";
import { useState } from "react";
import {
  entryPath,
  subPath,
  FIRMA_ENTRY_FORMATS,
  TAETIGKEIT_FUER_OPTIONS,
  TAETIGKEIT_FUER_MIT_DETAIL,
  RECHTSFORM_OPTIONS,
} from "@kmvs/forms-core";
import { BRAND } from "./tokens";
import EntryField from "./EntryField";
import type { FieldDef, FirmaEntry } from "./types";

/**
 * Die Firmenliste hinter `firmaEntries` — „Unternehmen im Verbund" und
 * „Beteiligungen ausserhalb des Verbunds" (Excel-Abschnitt
 * „Unternehmensverbund u. Beteiligungen").
 *
 * **Warum eine Liste und nicht die Frage in Schleife.** Die Excel schreibt bei
 * Nr. 250 und Nr. 9 „wiederholte Abfrage bis ‚nein'" — am Papierbogen die
 * einzige Art, eine unbekannte Anzahl abzufragen. Am Bildschirm wäre dieselbe
 * Ja/Nein-Frage nach jeder Firma erneut eine Zumutung: Der Berater sieht
 * nicht, was er schon erfasst hat, und kann nichts korrigieren, ohne von vorn
 * zu beginnen. Die Liste stellt das Gate einmal und zeigt danach alle Firmen
 * nebeneinander — fachlich dasselbe, nur ohne die Schleife.
 *
 * **Warum die Firmen hier nicht direkt Kontakte anlegen.** Für jede Firma im
 * Verbund soll später eine eigene Analyse ausgefüllt werden (PO 20.08.2026).
 * Bis dahin bleibt der Eintrag im Antwortsatz und trägt mit
 * `uebernommenAlsContactId` die Stelle, an der die Übernahme ihn wiederfindet
 * — dasselbe Verfahren wie bei den externen Verantwortlichkeiten
 * (`AnalysemodusAuswertungPage`).
 */
const useStyles = makeStyles({
  description: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    marginBottom: "12px",
  },
  addBar: {
    backgroundColor: "#fff",
    ...shorthands.padding("12px"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#e9e4ee"),
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  addBarRow: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "& > *": { minWidth: 0 },
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr auto",
      alignItems: "end",
    },
  },
  addBarLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#5d5664",
    marginBottom: "4px",
    display: "block",
  },
  addBarHint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  card: {
    backgroundColor: "#faf8fb",
    ...shorthands.padding("16px"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#e9e4ee"),
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    marginBottom: "12px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 600,
    color: BRAND.ink,
  },
  cardBadge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: "#fff",
    ...shorthands.padding("3px", "10px"),
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", "#e9e4ee"),
  },
  topGrid: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "& > *": { minWidth: 0 },
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  fieldLabel: {
    display: "block",
    minWidth: 0,
  },
  fieldLabelText: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "3px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  sectionLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  rollenGrid: {
    display: "grid",
    gap: "6px",
    gridTemplateColumns: "1fr",
    marginTop: "6px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
  },
});

const makeId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `fe-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

type Props = {
  field: FieldDef;
  value: FirmaEntry[];
  onChange: (next: FirmaEntry[]) => void;
};

const FirmenVerbundList = ({ field, value, onChange }: Props) => {
  const styles = useStyles();
  /** Verbindungsarten (Nr. 278/280) — vom Schema mitgegeben. */
  const verbindungOptions = field.options ?? [];
  /** Rollen (Nr. 277/281) — Mehrfachauswahl. */
  const rollenOptions = field.functionOptions ?? [];
  /** Nur der Verbund fragt „für wen erfolgt die Tätigkeit?" (Nr. 274/276). */
  const zeigtTaetigkeitFuer = field.withTaetigkeitFuer === true;

  const [pendingName, setPendingName] = useState<string>("");

  const entries = value;

  const update = (id: string, patch: Partial<FirmaEntry>) => {
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };
  const remove = (id: string) => {
    onChange(entries.filter((e) => e.id !== id));
  };
  const add = () => {
    const name = pendingName.trim();
    if (!name) return;
    onChange([...entries, { id: makeId(), firmenname: name }]);
    setPendingName("");
  };
  /** Rolle an- oder abwählen — `rollen` bleibt dabei immer ein Array. */
  const toggleRolle = (entry: FirmaEntry, rolle: string) => {
    const aktuell = entry.rollen ?? [];
    const naechste = aktuell.includes(rolle)
      ? aktuell.filter((r) => r !== rolle)
      : [...aktuell, rolle];
    update(entry.id, { rollen: naechste });
  };

  return (
    <div>
      {field.description && (
        <span className={styles.description}>{field.description}</span>
      )}

      <div className={styles.addBar}>
        <span className={styles.addBarHint}>
          Firmierung eintragen, dann auf <strong>+ Hinzufügen</strong> klicken — danach die Angaben zur Firma ergänzen.
        </span>
        <div className={styles.addBarRow}>
          <label>
            <span className={styles.addBarLabel}>Firmierung</span>
            <input
              type="text"
              className={styles.input}
              value={pendingName}
              placeholder="z. B. Muster Besitz GmbH"
              onChange={(e) => setPendingName(e.target.value)}
              onKeyDown={(e) => {
                // Enter fügt hinzu, statt das ganze Formular abzuschicken.
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
          </label>
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            onClick={add}
            disabled={!pendingName.trim()}
          >
            Hinzufügen
          </Button>
        </div>
      </div>

      {entries.map((entry, idx) => (
        <div key={entry.id} className={styles.card}>
          <div className={styles.cardHeader}>
            <div style={{ display: "flex", alignItems: "center", columnGap: 10, flexWrap: "wrap" }}>
              <span className={styles.cardTitle}>
                {entry.firmenname?.trim() || `Unternehmen ${idx + 1}`}
              </span>
              {entry.uebernommenAlsContactId && (
                <span className={styles.cardBadge} style={{ color: "#1f7a3c" }}>
                  ✓ übernommen
                </span>
              )}
            </div>
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              onClick={() => remove(entry.id)}
              aria-label="Unternehmen entfernen"
            >
              Entfernen
            </Button>
          </div>

          {/* Firmierung und Anschrift — Excel Nr. 251 / 255. */}
          <div>
            <span className={styles.sectionLabel}>Firmierung und Anschrift</span>
            <div className={styles.topGrid} style={{ marginTop: 6 }}>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldLabelText}>Firmierung *</span>
                <input
                  type="text"
                  className={styles.input}
                  value={entry.firmenname ?? ""}
                  onChange={(e) => update(entry.id, { firmenname: e.target.value })}
                />
              </label>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldLabelText}>Rechtsform</span>
                <select
                  className={styles.input}
                  value={entry.gesellschaftsform ?? ""}
                  onChange={(e) =>
                    update(entry.id, { gesellschaftsform: e.target.value })
                  }
                >
                  <option value="">— wählen —</option>
                  {RECHTSFORM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.fieldLabel} style={{ gridColumn: "1 / -1" }}>
                <span className={styles.fieldLabelText}>Anschrift</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Straße, PLZ, Ort"
                  value={entry.anschrift ?? ""}
                  onChange={(e) => update(entry.id, { anschrift: e.target.value })}
                />
              </label>
            </div>
          </div>

          {/* Rolle des Unternehmens — Excel Nr. 277 / 281, Mehrfachauswahl. */}
          {rollenOptions.length > 0 && (
            <div>
              <span className={styles.sectionLabel}>
                Rolle des Unternehmens (Mehrfachauswahl)
              </span>
              <div className={styles.rollenGrid}>
                {rollenOptions.map((o) => (
                  <label key={o.value} className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={(entry.rollen ?? []).includes(o.value)}
                      onChange={() => toggleRolle(entry, o.value)}
                    />
                    <span>{o.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Verbindung und Quote — Excel Nr. 278/254 bzw. 280/258. */}
          <div>
            <span className={styles.sectionLabel}>Verbindung und Beteiligung</span>
            <div className={styles.topGrid} style={{ marginTop: 6 }}>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldLabelText}>Verbindung zu diesem Unternehmen</span>
                <select
                  className={styles.input}
                  value={entry.verbindung ?? ""}
                  onChange={(e) => update(entry.id, { verbindung: e.target.value })}
                >
                  <option value="">— wählen —</option>
                  {verbindungOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              {/* Prozentwert wird beim Verlassen geprüft und kanonisiert. */}
              <EntryField
                label="Beteiligung in %"
                listKey={field.key}
                pfad={subPath(entryPath(field.key, entry, idx), "anteil")}
                format={FIRMA_ENTRY_FORMATS.anteil}
                placeholder="z. B. 51"
                value={entry.anteil ?? ""}
                onChange={(next) => update(entry.id, { anteil: next })}
              />
            </div>
          </div>

          {/* Tätigkeit — Excel Nr. 253 / 257. */}
          <div>
            <span className={styles.sectionLabel}>Tätigkeit</span>
            <label className={styles.fieldLabel} style={{ marginTop: 6 }}>
              <span className={styles.fieldLabelText}>
                Was macht dieses Unternehmen?
              </span>
              <textarea
                className={styles.input}
                rows={3}
                value={entry.taetigkeit ?? ""}
                onChange={(e) => update(entry.id, { taetigkeit: e.target.value })}
              />
            </label>
          </div>

          {/* Für wen erfolgt die Tätigkeit — Excel Nr. 274/276, nur im Verbund. */}
          {zeigtTaetigkeitFuer && (
            <div>
              <span className={styles.sectionLabel}>Für wen erfolgt die Tätigkeit?</span>
              <div className={styles.topGrid} style={{ marginTop: 6 }}>
                <label className={styles.fieldLabel}>
                  <span className={styles.fieldLabelText}>Tätigkeit für</span>
                  <select
                    className={styles.input}
                    value={entry.taetigkeitFuer ?? ""}
                    onChange={(e) =>
                      update(entry.id, { taetigkeitFuer: e.target.value })
                    }
                  >
                    <option value="">— wählen —</option>
                    {TAETIGKEIT_FUER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {/* Nr. 276 fragt nur bei „innerhalb des Verbunds" nach. */}
                {TAETIGKEIT_FUER_MIT_DETAIL.includes(entry.taetigkeitFuer ?? "") && (
                  <label className={styles.fieldLabel}>
                    <span className={styles.fieldLabelText}>
                      Für welche Unternehmen im Verbund?
                    </span>
                    <input
                      type="text"
                      className={styles.input}
                      value={entry.taetigkeitFuerDetail ?? ""}
                      onChange={(e) =>
                        update(entry.id, { taetigkeitFuerDetail: e.target.value })
                      }
                    />
                  </label>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default FirmenVerbundList;
